// Client-side KYC format/checksum validators.
// Mirrors auth/server/lib/validators.js so inline validation matches the server
// (and the 400 "Neveljavna ..." responses never surprise the user at submit time).

// Slovenian EMŠO (13 digits) checksum validation.
export function isValidEmso(value) {
  if (!value) return false;
  const emso = String(value).trim();
  if (!/^\d{13}$/.test(emso)) return false;

  const weights = [7, 6, 5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(emso[i], 10) * weights[i];
  }
  let control = 11 - (sum % 11);
  if (control === 11) control = 0;
  if (control === 10) return false;
  return control === parseInt(emso[12], 10);
}

// Slovenian tax number (davčna številka): 8 digits, optional "SI" prefix.
export function isValidSiTaxNumber(value) {
  if (!value) return false;
  const tax = String(value).trim().toUpperCase().replace(/^SI/, '').replace(/\s/g, '');
  if (!/^\d{8}$/.test(tax)) return false;

  const weights = [8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    sum += parseInt(tax[i], 10) * weights[i];
  }
  let control = 11 - (sum % 11);
  if (control === 10) control = 0;
  if (control === 11) return false;
  return control === parseInt(tax[7], 10);
}

// Slovenian matična številka (company registration number): 7 or 10 digits.
export function isValidSiRegistrationNumber(value) {
  if (!value) return false;
  const reg = String(value).trim().replace(/\s/g, '');
  return /^\d{7}$/.test(reg) || /^\d{10}$/.test(reg);
}

// Slovenian postal code: 4 digits.
export function isValidSiPostalCode(value) {
  if (!value) return false;
  return /^\d{4}$/.test(String(value).trim());
}

export function isSlovenia(c) {
  return /slov/i.test(c || '');
}

// ---- Date helpers (UI uses dd/mm/yyyy; the DB stores ISO yyyy-mm-dd) ----

// True when `value` is a real calendar date in dd/mm/yyyy form.
export function isValidDisplayDate(value) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value || '').trim());
  if (!m) return false;
  const day = +m[1], month = +m[2], year = +m[3];
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return false;
  const d = new Date(year, month - 1, day);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}

// dd/mm/yyyy -> yyyy-mm-dd. Returns '' when the input is empty or invalid.
export function displayToIso(value) {
  if (!isValidDisplayDate(value)) return '';
  const [dd, mm, yyyy] = String(value).trim().split('/');
  return `${yyyy}-${mm}-${dd}`;
}

// yyyy-mm-dd (or ISO timestamp) -> dd/mm/yyyy. Returns '' when empty/invalid.
export function isoToDisplay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || '').trim());
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

// Progressive masking while typing: keep digits, insert slashes as dd/mm/yyyy.
export function maskDateInput(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join('/');
}

// Country-aware validators (mirrors of the server lib). Re-exported here so
// pages can import everything from one module.
export { isValidNationalId } from './nationalId';
export { isValidEuVat } from './euVat';
export { iso as countryIso } from './countryCodes';
